import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { ButtonBase, Grid, Typography } from '@material-ui/core';
import clsx from 'clsx';

import InputProps from '../InputProps';
import DisplayItem from '../../../types/DisplayItem';
import StyledSlider from '../helpers/StyledSlider';
import BaseInputWrapper from '../BaseInputWrapper';

import { generateProperties } from '../helpers';

import { colors } from '../../../theme';

const useStyles = makeStyles({
  tickLabelContainer: {
    position: 'relative',
    width: '100%',
    height: 18,
    marginTop: 12,
  },
  disabledTickText: {
    color: colors.silver,
  },
  tickLabel: {
    maxWidth: 90,
    textAlign: 'center',
  },
  disabledActiveMark: {
    backgroundColor: `${colors.gallery} !important`,
    borderColor: `${colors.gallery} !important`,
  },
  disabledTrack: {
    backgroundColor: `${colors.gallery} !important`,
  },
});

type LabelTypes = 'all' | 'ends';
export interface SliderProps extends InputProps<string, DisplayItem> {
  /** The items to display on the slide */
  options: DisplayItem[];
  /** End = only the initial and final labels are visible */
  labelType?: LabelTypes;
}

/**
 * Returns the amount of pixels that must be set as the "left" attribute for the label in the
 * provided offset.
 *
 * @param index - The offset to use as reference
 * @param options - The option source list
 */
function leftForIndex(index: number, options: DisplayItem[]): number {
  return index <= 0 ? 0 : (index / (options.length - 1)) * 100;
}

/**
 * Returns the world wrap css declaration needed for the provided option
 *
 * @param option - The option to check
 */
function wordWrapForOption(option: DisplayItem): string {
  return typeof option.Text === 'string' && option.Text.length < 15
    ? 'nowrap'
    : 'normal';
}

/**
 * Returns the required transform css declaration for the label at the provided index
 *
 * @param index - The offset to use as reference
 * @param options - The option source list
 */
function transformForIndex(index: number, options: DisplayItem[]): string {
  if (index === 0) {
    return 'translateY(-50%)';
  }
  if (index === options.length - 1) {
    return 'translateX(-100%) translateY(-50%)';
  }
  return 'translateX(-50%) translateY(-50%)';
}

/**
 * Returns true if the label for the specified option should show, depending on what was configured for
 * the component as the `labelType` parameter.
 *
 * @param option - The option to check
 * @param labelType - The label type display logic provided to the component
 * @param options - The option source list
 */
function tickLabelShouldShow(
  option: DisplayItem,
  labelType: LabelTypes,
  options: DisplayItem[],
): boolean {
  if (labelType === 'all') {
    return true;
  }
  return (
    options.indexOf(option) === 0 ||
    options.indexOf(option) === options.length - 1
  );
}

function textAlignForLabel(
  option: DisplayItem,
  options: DisplayItem[],
): 'left' | 'right' | 'center' {
  if (options.indexOf(option) === 0) {
    return 'left';
  }
  if (options.indexOf(option) === options.length - 1) {
    return 'right';
  }
  return 'center';
}

const Slider = ({
  inputId,
  inputName,
  onChange,
  options,
  defaultValue = '',
  inputError = false,
  inputErrorMessage = '',
  isDisabled = false,
  labelType = 'ends',
  dataSource,
}: SliderProps): JSX.Element => {
  const formInputProps = generateProperties<string, DisplayItem>({
    inputName,
    onChange,
    defaultValue,
    inputError,
    inputErrorMessage,
    dataSource,
  });

  const classes = useStyles();
  const stepQuantity = options.length;

  function getOffsetForValue(value: string): number {
    const foundOption = options.find((item): boolean => item.Value === value);
    return foundOption ? options.indexOf(foundOption) : 0;
  }

  // Disable this check on the next line, since we don't actually care what
  // the type of _event actually is. (Note: this mirrors the example given in
  // material-ui's official documentation)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChange = (_event: any, newValue: number | number[]): void => {
    if (typeof newValue !== 'number') {
      return;
    }
    const prevOffset = getOffsetForValue(formInputProps.value);
    if (prevOffset !== newValue) {
      formInputProps.onChange(options[newValue]);
    }
  };

  function generateCssForLabelTick(
    option: DisplayItem,
  ): { [key: string]: string | number } {
    const itemIndex = options.indexOf(option);
    return {
      position: 'absolute',
      left: `${leftForIndex(itemIndex, options)}%`,
      transform: transformForIndex(itemIndex, options),
      whiteSpace: wordWrapForOption(option),
      minWidth: wordWrapForOption(option) === 'normal' ? 90 : 0,
    };
  }

  return (
    <BaseInputWrapper
      inputId={inputId}
      label=""
      error={formInputProps.error}
      errorMessage={formInputProps.errorText}
      disabled={isDisabled}
    >
      <Grid container>
        <Grid item xs={12}>
          <StyledSlider
            id={inputId}
            name={formInputProps.name}
            value={getOffsetForValue(formInputProps.value)}
            onChange={handleChange}
            step={1}
            marks
            min={0}
            max={stepQuantity - 1}
            disabled={isDisabled}
            classes={{
              markActive: isDisabled ? classes.disabledActiveMark : '',
              track: isDisabled ? classes.disabledTrack : '',
            }}
          />
        </Grid>
        <Grid item xs={12} className={classes.tickLabelContainer}>
          {options.map(
            (curOption: DisplayItem, index: number): React.ReactNode => (
              <Typography
                variant="caption"
                data-testid={`slider-option-${curOption.Value}`}
                key={curOption.Value}
                className={clsx(
                  classes.tickLabel,
                  isDisabled ? classes.disabledTickText : '',
                )}
                style={generateCssForLabelTick(curOption)}
              >
                <ButtonBase
                  id={`${inputId}-${index}`}
                  disableRipple
                  style={{
                    textAlign: textAlignForLabel(curOption, options),
                    display: 'inline-block',
                  }}
                  onClick={(e: unknown): void => {
                    handleChange(e, index);
                  }}
                >
                  {tickLabelShouldShow(curOption, labelType, options) &&
                    curOption.Text}
                </ButtonBase>
              </Typography>
            ),
          )}
        </Grid>
      </Grid>
    </BaseInputWrapper>
  );
};

export default Slider;
